import { FacilityPermissionsRequiringWrite } from "encamp-shared/src/constants/permissionsTypes";
import { Permission } from "generated-graphql/graphql";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";

import { useCurrentUser } from "./useCurrentUser";
import { useTenant } from "./useTenant";

export const useAuthorization = () => {
  const { tenantId, loading: loadingTenant } = useTenant();
  const { user, isStaff, isEmulating, loading: loadingUser } = useCurrentUser();
  // Real staff users keep the existing all-permissions convention. When
  // permissions are being emulated, use the emulated role permissions instead.
  const hasStaffPermissionOverride = isStaff && !isEmulating;

  // Get the user tenant for the current tenant (including partner relationships)
  const userTenant = useMemo(() => {
    const naiveUserTenant = user?.UserTenant.find((ut) => ut?.tenantId === tenantId);
    // If the user is associated via partner, we want to look at the role from the parent tenant
    const parentUserTenant = user?.UserTenant.find((ut) =>
      ut.tenant?.partnersTenants.some((pt) => pt.id === tenantId)
    );
    return naiveUserTenant ?? parentUserTenant;
  }, [user, tenantId]);

  // The union of the user's permissions from their tenant and the partner tenant
  const userPermissions = useMemo(() => {
    const naiveUserTenant = user?.UserTenant.find((ut) => ut?.tenantId === tenantId);
    // If the user is associated via partner, we want to look at the role from the parent tenant
    const parentUserTenant = user?.UserTenant.find((ut) =>
      ut.tenant?.partnersTenants.some((pt) => pt.id === tenantId)
    );
    return Array.from(
      new Set([
        ...(naiveUserTenant?.role?.permissions ?? []),
        ...(parentUserTenant?.role?.permissions ?? []),
      ])
    );
  }, [user, tenantId]);

  const hasPermissions = useCallback(
    (permissions: Permission[]) => {
      if (hasStaffPermissionOverride) {
        return true;
      }

      return permissions.every((p) => userPermissions.includes(p));
    },
    [userPermissions, hasStaffPermissionOverride]
  );

  /**
   * @returns a tuple of [boolean, string[]] where the boolean indicates
   * whether the current user has access to the facility and the string[]
   * is a list of their permissions for the facility.
   */
  const permissionsForFacility = useCallback(
    (facilityId: string): [boolean, Permission[]] => {
      if (hasStaffPermissionOverride) {
        return [true, []];
      }
      // If the user has write permissions to all facilities, they can access any facility with all of their permissions
      if (userPermissions.includes(Permission.WriteAllFacility)) {
        return [true, userPermissions];
      }

      // If the user is directly associated with the facility, they have access with all of their permissions
      if (user?.facilities.find((f) => f.id === facilityId)) {
        return [true, userPermissions];
      }

      // ReadAllFacility supplies scope, except for permissions requiring direct facility write access.
      if (userPermissions.includes(Permission.ReadAllFacility)) {
        return [
          true,
          userPermissions.filter(
            (p) => !(FacilityPermissionsRequiringWrite as string[]).includes(p)
          ),
        ];
      }

      return [false, []];
    },
    [user, userPermissions, hasStaffPermissionOverride]
  );

  const hasPermissionForFacility = useCallback(
    (facilityId: string, permissions: Permission[]): boolean => {
      if (hasStaffPermissionOverride) {
        return true;
      }
      const [hasAccess, facilityPermissions] = permissionsForFacility(facilityId);
      return hasAccess && permissions.every((p) => facilityPermissions.includes(p));
    },
    [hasStaffPermissionOverride, permissionsForFacility]
  );

  const hasWritePermissionForFacility = useCallback(
    (facilityId: string) => {
      if (hasPermissions([Permission.WriteAllFacility])) return true;
      if (user?.facilities.find((f) => f.id === facilityId)) return true;
      return false;
    },
    [hasPermissions, user?.facilities]
  );

  const hasTaskWritePermissionForFacilities = useCallback(
    (facilityIds: string[]) => {
      if (hasPermissions([Permission.WriteAllFacility])) return true;
      if (
        facilityIds.length > 0 &&
        facilityIds.every((fid) => user?.facilities.find((f) => f.id === fid))
      )
        return true;
      return false;
    },
    [hasPermissions, user?.facilities]
  );

  /**
   * @returns the current user's role name for the current tenant, or undefined if no role is found
   */
  const getUserRole = useCallback((): string | undefined => {
    if (hasStaffPermissionOverride) {
      // Staff users don't have traditional roles
      return undefined;
    }
    return userTenant?.role?.name;
  }, [userTenant, hasStaffPermissionOverride]);

  return useMemo(
    () => ({
      hasPermissions,
      hasPermissionForFacility,
      hasWritePermissionForFacility,
      hasTaskWritePermissionForFacilities,
      getUserRole,
      loading: loadingTenant || loadingUser,
    }),
    [
      hasPermissions,
      hasPermissionForFacility,
      hasWritePermissionForFacility,
      hasTaskWritePermissionForFacilities,
      getUserRole,
      loadingTenant,
      loadingUser,
    ]
  );
};

/**
 * Convenience hook: returns whether the current user can edit the facility.
 *
 * @param facilityIdOverride Optional facility ID. Defaults to the `:facilityId`
 *   path parameter. Callers without a `:facilityId` route param (e.g. the
 *   submission stepper) can pass the id explicitly. The underlying permission
 *   check is route-independent, so this is backward-compatible — existing
 *   callers pass nothing and keep the path-param behavior.
 */
export function useCanEditFacility(facilityIdOverride?: string): boolean {
  const { facilityId: paramFacilityId } = useParams<{ facilityId: string }>();
  const facilityId = facilityIdOverride ?? paramFacilityId;
  const { hasWritePermissionForFacility } = useAuthorization();
  return facilityId ? hasWritePermissionForFacility(facilityId) : false;
}

/**
 * Convenience hook: returns whether the current user can change the facility's
 * billing contact (the "Use Encamp as billing contact" setting and the EPCRA
 * billing contact role). Some tenants lock this down so only Encamp can change
 * it; the server enforces the same permission.
 */
export function useCanEditFacilityBillingContact(facilityId: string | undefined): boolean {
  const { hasPermissionForFacility } = useAuthorization();
  return facilityId
    ? hasPermissionForFacility(facilityId, [Permission.WriteFacilityBillingContact])
    : false;
}
